import { type NotificationDelivery } from '@aysar/contracts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCopyToClipboard,
  type CopyStatus,
} from '@/shared/hooks/useCopyToClipboard/useCopyToClipboard';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { type ModalData } from '@/shared/store/reducers/modal/modal.types';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import { useDeliveryQuery } from '../../services/notifications/notifications.api';

export interface UseDeliveryDetailModalLogic {
  delivery: NotificationDelivery | null;
  isLoading: boolean;
  error: ApiError | null;
  /** The dialog was opened BY a refused retry, and has to say why. */
  wasRetryRefused: boolean;
  reload: () => void;
  /** The relay's own id — what a support role quotes to the vendor. */
  copyStatus: CopyStatus;
  copyProviderMessageId: () => void;
}

/**
 * One delivery, read fresh.
 *
 * A separate request rather than the row the log already holds: the list polls,
 * so the row a dialog was opened from may be a version the operator can no
 * longer act on — and the one question this dialog exists to answer ("what
 * exactly did we send them?") must not be answered from a stale copy.
 *
 * `refusedRetry` arrives in the modal payload because this dialog is what a 409
 * opens: the worker claimed the row first, and the honest answer is the row's
 * current state rather than a toast that disappears (P19b).
 */
export function useDeliveryDetailModalLogic(
  modalData: ModalData | undefined,
): UseDeliveryDetailModalLogic {
  const { t } = useTranslation('notifications');
  const toast = useToast();
  const { copy, status: copyStatus } = useCopyToClipboard();
  const deliveryId = readString(modalData, 'deliveryId');
  const query = useDeliveryQuery(deliveryId ?? '', { skip: deliveryId === null });

  const providerMessageId = query.data?.providerMessageId ?? null;

  const copyProviderMessageId = useCallback(() => {
    if (providerMessageId === null) {
      return;
    }

    void copy(providerMessageId).then((copied) => {
      if (!copied) {
        toast.error(t('deliveries.detail.copyFailed'));
      }
    });
  }, [providerMessageId, copy, toast, t]);

  return {
    delivery: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error === undefined ? null : toApiError(query.error),
    wasRetryRefused: modalData?.['refusedRetry'] === true,
    reload: () => {
      void query.refetch();
    },
    copyStatus,
    copyProviderMessageId,
  };
}

function readString(data: ModalData | undefined, key: string): string | null {
  const value = data?.[key];

  return typeof value === 'string' && value !== '' ? value : null;
}
